import { useEffect, useMemo } from "react";

export const useEditorModuleData = ({
  claimsArray,
  reusableTextIdsArray,
  droppedComponentIds,
  droppedTextIds,
  mustUseDataAvailable,
  droppedTitleIds,
  optionalModules,
  layerCompData,
  checkAllMustUseData,
  setMustUseData,
  setClaimsArray,
  setDroppedComponentIds,
  setDroppedComponentTextIds,
  setDroppedTextIds,
  setClaimReferencesFoonotes,
  setScrollClaimReferencesFoonotes,
  setNotUsedEditorToggle,
  veevaDocumentNumber,
  selectedSizeOptions,
  saveBannerValidationCheckDetails,
  setEditModuleClicked,
  deleteModulesCanvas,
  ErrComponentToRender,
}) => {
  const handleChangeSizeRefereshModulesData = () => {
    setClaimsArray([]);
    setDroppedComponentIds([]);
    setDroppedComponentTextIds([]);
    setDroppedTextIds([]);
    setClaimReferencesFoonotes([]);
    setScrollClaimReferencesFoonotes([]);
    setNotUsedEditorToggle(false);
  };

  const moduleUsedIds = useMemo(
    () => ({
      claimsArray,
      reusableTextIdsArray,
      droppedComponentIds,
      droppedTextIds,
      mustUseDataAvailable,
      droppedTitleIds,
    }),
    [
      claimsArray,
      reusableTextIdsArray,
      droppedComponentIds,
      droppedTextIds,
      mustUseDataAvailable,
      droppedTitleIds,
    ],
  );

  useEffect(() => {
    let moduleClaimsDataArray = mustUseDataAvailable;
    const processMustUseData = (dataArray) => {
      if (!dataArray) return [];
      let filteredModulesDataArray = [];

      filteredModulesDataArray = dataArray.map((module) => {
        const updatedClaims = (module?.claims || [])
          .filter((claim) => claim.mustUse && claim.mlrStatus !== "Expired")
          .map((claim) => {
            const isClaimUsed = claimsArray.includes(claim.claimId);

            const filteredRelatedClaims = (claim.relatedClaims || []).filter(
              (relatedClaim) =>
                relatedClaim.mlrStatus !== "Expired" &&
                !claimsArray.includes(relatedClaim.claimId),
            );

            if (isClaimUsed && filteredRelatedClaims.length === 0) {
              return null;
            }

            return {
              ...claim,
              isClaimUsed,
              relatedClaims: filteredRelatedClaims,
            };
          })
          .filter(Boolean);

        const updatedReusableTexts = (module.reusableTexts || []).filter(
          (reusableText) =>
            reusableText.mustUse &&
            reusableText.mlrStatus !== "Expired" &&
            !reusableTextIdsArray.includes(reusableText.reusableTextId),
        );

        const updatedComponents = (module.components || [])
          .filter((c) => c.mlrStatus !== "Expired")
          .map((component) => {
            let componentData = { ...component };

            componentData.isComponentUsed =
              droppedComponentIds.includes(component.componentId) || false;

            if (component.mustUse) {
              if (droppedComponentIds.includes(component.componentId)) {
                componentData.componentUrl = "";
              }

              if (
                component.subType === "Image" ||
                droppedTitleIds.includes(component.componentId)
              ) {
                componentData.title = "";
              }

              componentData.relatedClaims = (
                component.relatedClaims || []
              ).filter(
                (rc) =>
                  rc.mlrStatus !== "Expired" &&
                  !droppedComponentIds.includes(rc.claimId) &&
                  !droppedTextIds.includes(rc.id),
              );

              componentData.relatedReusableTexts = (
                component.relatedReusableTexts || []
              ).filter(
                (rt) =>
                  !droppedComponentIds.includes(rt.id) &&
                  !droppedTextIds.includes(rt.id),
              );

              const isEmpty =
                !componentData.title &&
                !componentData.componentUrl &&
                componentData.relatedReusableTexts.length === 0 &&
                componentData.relatedClaims.length === 0;

              return isEmpty ? null : componentData;
            } else {
              if (!componentData.isComponentUsed) return null;

              if (droppedComponentIds.includes(component.componentId)) {
                componentData.componentUrl = "";
              }

              if (
                component.subType === "Image" ||
                droppedTitleIds.includes(component.componentId)
              ) {
                componentData.title = "";
              }

              componentData.relatedClaims = (
                component.relatedClaims || []
              ).filter(
                (rc) =>
                  rc.mlrStatus !== "Expired" &&
                  !droppedComponentIds.includes(rc.claimId) &&
                  !droppedTextIds.includes(rc.id),
              );

              componentData.relatedReusableTexts = (
                component.relatedReusableTexts || []
              ).filter(
                (rt) =>
                  !droppedComponentIds.includes(rt.id) &&
                  !droppedTextIds.includes(rt.id),
              );

              return componentData;
            }
          })
          .filter(Boolean);

        return {
          ...module,
          claims: updatedClaims,
          reusableTexts: updatedReusableTexts,
          components: updatedComponents,
        };
      });
      return filteredModulesDataArray;
    };
    let processedMustUse = [];

    if (moduleClaimsDataArray) {
      processedMustUse = processMustUseData(moduleClaimsDataArray);
    }

    const optionalModulesToProcess = optionalModules?.filter((module) => {
      if (layerCompData?.length > 0) {
        const isModuleDataDroppedInEditor = layerCompData.some(
          (droppedComponent) => {
            if (droppedComponent.componentType === "Scrollbar") {
              return droppedComponent.scrollbarComponents.some((scrollComp) => {
                if (scrollComp.moduleIdIdentifier === module.moduleDamName) {
                  return true;
                }
              });
            } else {
              return droppedComponent.moduleIdIdentifier === module.moduleDamName;
            }
          },
        );
        if (!isModuleDataDroppedInEditor) {
          return false;
        }
      }

      return (
        module?.claims?.some(
          (claim) =>
            claimsArray.includes(claim.claimId) ||
            claim?.relatedClaims?.some((rc) =>
              claimsArray.includes(rc.claimId),
            ),
        ) ||
        module?.reusableTexts?.some((reusableText) =>
          reusableTextIdsArray.includes(reusableText.reusableTextId),
        ) ||
        module?.components?.some(
          (component) =>
            droppedComponentIds.includes(component.componentId) ||
            droppedTextIds.includes(component.componentId) ||
            component?.relatedClaims?.some(
              (rc) =>
                droppedComponentIds.includes(rc.claimId) ||
                droppedTextIds.includes(rc.id),
            ) ||
            component?.relatedReusableTexts?.some(
              (rt) =>
                droppedTextIds.includes(rt.id) ||
                droppedComponentIds.includes(rt.id),
            ),
        )
      );
    });

    if (optionalModulesToProcess?.length > 0) {
      checkAllMustUseData(optionalModulesToProcess);
      const processedOptionalMustUseData = processMustUseData(
        optionalModulesToProcess,
      ).map((item) => ({ ...item, optionalMustUseFlag: true }));

      processedMustUse = [...processedMustUse, ...processedOptionalMustUseData];
    }

    setMustUseData(processedMustUse);
  }, [moduleUsedIds]);

  const deleteModulesOnCanvas = () => {
    deleteModulesCanvas(
      veevaDocumentNumber,
      async (response, err, errorCode) => {
        const data = await response.data;

        if (err) {
          const errCoponent = ErrComponentToRender(errorCode);
        } else {
          setEditModuleClicked(data);
        }
      },
    );

    for (const bannerItem of selectedSizeOptions) {
      const validationObj = [
        {
          assetId: bannerItem.value,
          isValid: false,
        },
      ];
      saveBannerValidationCheckDetails(validationObj);
    }
  };

  return {
    handleChangeSizeRefereshModulesData,
    deleteModulesOnCanvas,
  };
};
